import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, PackageCheck, AlertTriangle, Camera, XCircle } from 'lucide-react';
import { useReceiveTransfer, type ReceiveTransferInput, type ReceiveTransferItemInput } from '../../../api/mutations/transfer.mutations';
import type { Transfer } from '../../../api/queries/transfer.queries';

interface ReceiveTransferModalProps {
  transfer: Transfer;
  onClose: () => void;
  onSuccess: () => void;
}

interface ReceiveItem {
  itemId: string;
  ingredientName: string;
  dispatchedQuantity: number;
  receivedQuantity: number;
  unit: string;
  condition: ReceiveTransferItemInput['condition'];
  isRejected: boolean;
  rejectedQuantity: number;
  rejectionReason: string;
  rejectionPhotoUrls: string[];
  notes: string;
}

const CONDITIONS: ReceiveTransferItemInput['condition'][] = [
  'good',
  'damaged',
  'expired',
  'wrong_item',
  'quantity_short',
  'quality_issue',
];

export default function ReceiveTransferModal({ transfer, onClose, onSuccess }: ReceiveTransferModalProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<ReceiveItem[]>(
    transfer.items.map((item) => {
      const ingredient = typeof item.ingredientId === 'object' ? item.ingredientId : null;
      const qty = item.dispatchedQuantity || item.approvedQuantity || item.requestedQuantity;
      return {
        itemId: item._id,
        ingredientName: ingredient?.name?.en || '-',
        dispatchedQuantity: qty,
        receivedQuantity: qty,
        unit: item.unit,
        condition: 'good',
        isRejected: false,
        rejectedQuantity: 0,
        rejectionReason: '',
        rejectionPhotoUrls: [],
        notes: '',
      };
    })
  );
  const [receivingPhotos, _setReceivingPhotos] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const receiveTransfer = useReceiveTransfer(transfer._id);

  const updateItem = (index: number, updates: Partial<ReceiveItem>) => {
    setItems(items.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, ...updates };

      // Auto-set rejection based on condition
      if (updates.condition && updates.condition !== 'good') {
        updated.isRejected = true;
        if (updated.rejectedQuantity === 0) {
          updated.rejectedQuantity = updated.dispatchedQuantity - updated.receivedQuantity;
        }
      }

      return updated;
    }));
  };

  const hasDiscrepancies = items.some(
    (item) =>
      item.receivedQuantity !== item.dispatchedQuantity ||
      item.condition !== 'good' ||
      item.isRejected
  );

  const hasRejections = items.some((item) => item.isRejected);

  const handleReceive = async () => {
    // Validate rejections have reasons
    for (const item of items) {
      if (item.isRejected && !item.rejectionReason.trim()) {
        alert(t('transfers.rejectionReasonRequired'));
        return;
      }
    }

    try {
      const payload: ReceiveTransferInput = {
        items: items.map((item) => ({
          itemId: item.itemId,
          receivedQuantity: item.receivedQuantity,
          condition: item.condition,
          isRejected: item.isRejected,
          rejectedQuantity: item.isRejected ? item.rejectedQuantity : undefined,
          rejectionReason: item.isRejected ? item.rejectionReason : undefined,
          rejectionPhotoUrls: item.rejectionPhotoUrls.length > 0 ? item.rejectionPhotoUrls : undefined,
          notes: item.notes || undefined,
        })),
        receivingPhotos: receivingPhotos.length > 0 ? receivingPhotos : undefined,
        notes: notes || undefined,
      };

      await receiveTransfer.mutateAsync(payload);
      onSuccess();
    } catch (error) {
      console.error('Failed to receive transfer:', error);
    }
  };

  const fromLocation = typeof transfer.fromLocationId === 'object' ? transfer.fromLocationId : null;
  const toLocation = typeof transfer.toLocationId === 'object' ? transfer.toLocationId : null;

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'good':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'damaged':
      case 'expired':
      case 'wrong_item':
      case 'quality_issue':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'quantity_short':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('transfers.receiveTransfer')}</h2>
            <span className="text-sm text-muted-foreground">({transfer.transferNumber})</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Transfer Info */}
            <div className="p-4 rounded-lg bg-secondary/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('transfers.from')}</p>
                  <p className="font-medium">{fromLocation?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('transfers.to')}</p>
                  <p className="font-medium">{toLocation?.name || '-'}</p>
                </div>
              </div>
            </div>

            {/* Discrepancy Warning */}
            {hasDiscrepancies && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="text-sm">{t('transfers.discrepancyWarning')}</p>
              </div>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('transfers.itemsToReceive')}</h3>
              <div className="space-y-4">
                {items.map((item, index) => {
                  const hasIssue = item.receivedQuantity !== item.dispatchedQuantity || item.condition !== 'good';
                  return (
                    <div
                      key={item.itemId}
                      className={`p-4 rounded-lg border ${hasIssue ? 'border-yellow-300 bg-yellow-50' : 'bg-card'}`}
                    >
                      <div className="grid grid-cols-6 gap-4 items-start">
                        {/* Ingredient Name */}
                        <div className="col-span-2">
                          <p className="text-sm font-medium">{item.ingredientName}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('transfers.dispatched')}: {item.dispatchedQuantity} {item.unit}
                          </p>
                        </div>

                        {/* Received Quantity */}
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            {t('transfers.received')}
                          </label>
                          <input
                            type="number"
                            value={item.receivedQuantity}
                            onChange={(e) =>
                              updateItem(index, {
                                receivedQuantity: Math.max(0, parseFloat(e.target.value) || 0),
                              })
                            }
                            className={`w-full rounded border bg-background px-2 py-1 text-right ${
                              item.receivedQuantity !== item.dispatchedQuantity ? 'border-yellow-400' : ''
                            }`}
                            min={0}
                            step={0.1}
                          />
                        </div>

                        {/* Condition */}
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            {t('transfers.condition')}
                          </label>
                          <select
                            value={item.condition}
                            onChange={(e) =>
                              updateItem(index, { condition: e.target.value as ReceiveItem['condition'] })
                            }
                            className={`w-full rounded border px-2 py-1 ${getConditionColor(item.condition || 'good')}`}
                          >
                            {CONDITIONS.map((cond) => (
                              <option key={cond} value={cond}>
                                {t(`transfers.${cond}`)}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Reject Toggle */}
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            {t('transfers.reject')}
                          </label>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.isRejected}
                              onChange={(e) => updateItem(index, { isRejected: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                          </label>
                        </div>

                        {/* Notes */}
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1">
                            {t('common.notes')}
                          </label>
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateItem(index, { notes: e.target.value })}
                            className="w-full rounded border bg-background px-2 py-1"
                            placeholder={t('common.optional')}
                          />
                        </div>
                      </div>

                      {/* Rejection Details */}
                      {item.isRejected && (
                        <div className="mt-4 pt-4 border-t border-red-200 grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-red-600 mb-1">
                              {t('transfers.rejectedQuantity')}
                            </label>
                            <input
                              type="number"
                              value={item.rejectedQuantity}
                              onChange={(e) =>
                                updateItem(index, {
                                  rejectedQuantity: Math.max(0, parseFloat(e.target.value) || 0),
                                })
                              }
                              className="w-full rounded border border-red-200 bg-background px-2 py-1 text-right"
                              min={0}
                              max={item.dispatchedQuantity}
                              step={0.1}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-red-600 mb-1">
                              {t('transfers.rejectionReason')} *
                            </label>
                            <input
                              type="text"
                              value={item.rejectionReason}
                              onChange={(e) => updateItem(index, { rejectionReason: e.target.value })}
                              className="w-full rounded border border-red-200 bg-background px-2 py-1"
                              placeholder={t('transfers.rejectionReasonPlaceholder')}
                              required
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rejection Summary */}
            {hasRejections && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <p className="text-sm font-medium text-red-800">{t('transfers.rejectionSummary')}</p>
                </div>
                <p className="text-sm text-red-700">
                  {items.filter((i) => i.isRejected).length} {t('transfers.itemsRejected')}
                </p>
                <p className="text-xs text-red-600 mt-1">{t('transfers.rejectionNote')}</p>
              </div>
            )}

            {/* Receiving Photos */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('transfers.receivingPhotos')}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors flex items-center gap-2"
                  onClick={() => {
                    // In a real implementation, this would open a file picker or camera
                    alert(t('transfers.photoUploadNotImplemented'));
                  }}
                >
                  <Camera className="h-4 w-4" />
                  {t('transfers.addPhoto')}
                </button>
                <span className="text-sm text-muted-foreground">
                  {receivingPhotos.length} {t('transfers.photosAdded')}
                </span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
                placeholder={t('transfers.receivingNotesPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleReceive}
            disabled={receiveTransfer.isPending}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <PackageCheck className="h-4 w-4" />
            {receiveTransfer.isPending ? t('common.saving') : t('transfers.confirmReceive')}
          </button>
        </div>
      </div>
    </div>
  );
}
